import { createTheme, Grid, Typography, useMediaQuery } from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import eyeLogo from "../../assets/images/logos/eyelogo.png";
import ProgressAnalysisEyeReport from "./ProgressAnaysisEyeReport";
import ReportInfoHeaderComponentAnalysis from "../../components/UI/ReportInfoSharedAnalysis";
import CommentsList from "../../components/UI/CommentsList";
import HeatmapChart from "../../components/UI/HeatmapChart";
import ProgressionCharts from "../../components/UI/ProgressionCharts";
import ProgressionChartLegend from "../../components/UI/ProgressionChartLegend";
import { ReportInfoShared } from "../../components/UI/ReportInfoShared";

// Contexts
import { ValidatePDFContext } from "../../contexts/ValidatePDFContext";

// Utils
import { sortByOldestDate } from "../../utils/dateHelper";
import { formatProgressionAnalysisData } from "../../utils/examReportsHelper";
import useClinic from "../../hooks/useClinic";

const ProgressionAnalysisReportContainer = ({
  report,
  patient,
  clinicId,
  progressionAnalysisId,
}) => {
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const bpTheme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1300,
        xl: 1700,
      },
    },
  });

  const isWiderThanSmallScreen = useMediaQuery(bpTheme.breakpoints.up("sm"));
  const isWiderThanMdScreen = useMediaQuery(bpTheme.breakpoints.up("md"));

  const gridSizeData = useMemo(() => [{ sm: 12 }], []);

  const isRightEye = useMemo(
    () =>
      report?.every((rep) => {
        const isRightRetinaLogikExam = rep?.reportData?.exam?.eye === "Right";
        const isRightExternalExam = rep?.eye === "Right";
        return isRightRetinaLogikExam || isRightExternalExam;
      }),
    [report]
  );

  const infoData = useMemo(
    () => [
      [
        {
          title: t("word_eye"),
          body: isRightEye ? t("word_right") : t("word_left"),
        },
      ],
    ],
    [isRightEye, t]
  );

  // sort eye chart displays from oldest to newest
  const sortedReport = useMemo(
    () => sortByOldestDate(report, "completionDate"),
    [report]
  );

  const progressionChartData = useMemo(() => {
    const sortedExams = sortByOldestDate(report, "completionDate");
    return formatProgressionAnalysisData(sortedExams, clinicTimezone);
  }, [report, clinicTimezone]);

  return (
    <Grid
      className="retinalogik-section"
      container
      columnSpacing={2}
      mt={5}
      gap={1}
      sx={{ display: "flex", alignItems: "start" }}
    >
      <Grid container item xs={12}>
        {forBackendPdf && (
          <Grid
            item
            xs={1}
            style={{
              alignItems: "center",
            }}
          >
            <img
              src={eyeLogo}
              alt="eye logo"
              style={{ width: "100%", height: "auto" }}
            />
          </Grid>
        )}

        <Grid container item xs={forBackendPdf ? 11 : 12} mb={1.5}>
          <Grid
            item
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            border={1}
            xs={12}
          >
            <Grid
              item
              sx={{
                display: "flex",
                background: "black",
                width: 70,
                height: "100%",
                color: "white",
                alignItems: "center",
                justifyContent: "center",
              }}
              xs={1.5}
              sm={1}
            >
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {isRightEye ? "OD" : "OS"}
              </Typography>
            </Grid>
            {
              <ReportInfoHeaderComponentAnalysis
                isWiderThanSmallScreen={isWiderThanSmallScreen}
                clinicId={clinicId}
                patient={patient}
                rightText={t("progression_analysis")}
                progressionAnalysisId={progressionAnalysisId}
              />
            }
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        item
        display="flex"
        justifyContent={{ xs: "space-around", md: "space-between" }}
        gap={6}
      >
        <Grid item display="flex" width="100%" justifyContent="space-between">
          <ReportInfoShared data={infoData} gridSizeData={gridSizeData} />
        </Grid>

        {/* <Grid
          item
          xs={12}
          md={6}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
          gap="8px"
          height="350px"
        >
          <HeatmapChart
            chartTitle={t("TODO: Add a proper title here")} // TODO: update with final value
            colorbarTitle={`${t("word_sensitivity")} (dB)`} // TODO: update with final value
            date="TODO add a date" // TODO: update with real data
            vrfi={0.71} // TODO: update with real data
            isRightEye={isRightEye}
          />
        </Grid> */}

        <Grid
          item
          display="flex"
          flexDirection="column"
          width="100%"
          gap="20px"
        >
          <Typography variant="h6">{t("Progression Charts")}</Typography>
          <ProgressionChartLegend />
          <ProgressionCharts
            chartData={progressionChartData}
            displayHorizontally={isWiderThanMdScreen && !forBackendPdf}
          />
        </Grid>

        <Grid item display="flex" flexDirection="column" width="100%" gap={6}>
          {sortedReport.map((item) => (
            <ProgressAnalysisEyeReport
              key={item.id}
              examReport={item.reportData}
              examData={item}
              forBackendPdf={forBackendPdf}
            />
          ))}
        </Grid>

        <Grid item xs={12}>
          <CommentsList isVisualField={false} />
        </Grid>
        <Box />
      </Grid>
    </Grid>
  );
};

export default ProgressionAnalysisReportContainer;
